import React, { useEffect, useState } from 'react';
import { Avatar, Box, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import DialogTemplate, { DialogSection } from '../../../layout/DialogTemplate';
import { adminApi, AdminMemberDetail } from '../../../../services/api/admin';
import { colors } from '../../../../styles/colors';

interface MemberDetailDialogProps {
  open: boolean;
  orgId: string;
  userId: string | null;
  onClose: () => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface LabeledFieldProps {
  label: string;
  value: string;
}

const LabeledField: React.FC<LabeledFieldProps> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: colors.text.secondary, display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const MemberDetailDialog: React.FC<MemberDetailDialogProps> = ({ open, orgId, userId, onClose }) => {
  const [detail, setDetail] = useState<AdminMemberDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setError(null);
    setDetail(null);
    adminApi
      .getMemberDetail(orgId, userId)
      .then(setDetail)
      .catch(() => setError('Daten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, orgId, userId]);

  return (
    <DialogTemplate open={open} onClose={onClose} title="Benutzer-Details" maxWidth="sm">
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {detail && !loading && (
        <>
          <DialogSection title="Stammdaten" first>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                src={detail.picture ?? undefined}
                alt={detail.name}
                sx={{ width: 48, height: 48, fontSize: '1.2rem' }}
              >
                {detail.name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {detail.name}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid size={6}>
                <LabeledField label="Vorname" value={detail.first_name ?? '–'} />
              </Grid>
              <Grid size={6}>
                <LabeledField label="Nachname" value={detail.last_name ?? '–'} />
              </Grid>
              <Grid size={12}>
                <LabeledField label="E-Mail" value={detail.email} />
              </Grid>
              <Grid size={6}>
                <LabeledField label="Erster Login" value={formatDate(detail.first_login)} />
              </Grid>
              <Grid size={6}>
                <LabeledField label="Letzter Login" value={formatDate(detail.last_login)} />
              </Grid>
            </Grid>
          </DialogSection>

          <DialogSection title="Rollen">
            {detail.roles.length === 0 ? (
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                Keine Rollen zugewiesen
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {detail.roles.map((role) => (
                  <Chip
                    key={role.id}
                    label={role.name}
                    size="small"
                    title={role.description ?? undefined}
                  />
                ))}
              </Box>
            )}
          </DialogSection>
        </>
      )}
    </DialogTemplate>
  );
};

export default MemberDetailDialog;
